import { useEffect, useMemo } from "react";
import { useTaskImages } from "@coworker/apprestructured/src/task-management/services/useTaskImages";
import { useBucketFiles } from "@coworker/apprestructured/src/task-management/services/useBucketFiles";

export const NO_TASK_IMAGE_URLS: string[] = [];

/**
 * Get object urls for all images in a given task.
 * These urls can be directly used as a src for an img element.
 *
 * The logic here is pretty complex but accomplishes these things:
 *  - Returns undefined both when fetching uploads and the actual data.
 *  - Use null object urls for images that fails to fetch.
 *  - Keeps the result as stable as possible in order to prevent flickering.
 *  - Revoke the object urls when they are no longer used.
 */
export function useTaskImageUrls(taskId: string | null) {
  const { data: uploads } = useTaskImages(taskId);
  const { data: blobs } = useBucketFiles(uploads || []);

  const objectUrls = useMemo(() => {
    if (blobs === undefined) {
      return undefined;
    } else if (blobs.length === 0) {
      return NO_TASK_IMAGE_URLS;
    } else {
      return blobs.map((blob) => {
        // Means the data upload has failed or not yet completed
        if (blob === null) {
          return null;
        }
        return URL.createObjectURL(blob);
      });
    }
  }, [blobs]);

  // Revoke the object urls when they are no longer used
  useEffect(() => {
    return () => {
      objectUrls?.forEach((objectUrl) => {
        if (objectUrl !== null) {
          URL.revokeObjectURL(objectUrl);
        }
      });
    };
  }, [objectUrls]);

  return objectUrls;
}
